import { CodedProperty, ensure, mapToCodedProperty, mapToNamedProperty } from "sprint-common-web";

import { GetCloudAssetDetailsResponse } from "../../../common/api/master-api/assets/details/GetCloudAssetDetailsResponse";
import { AspectRatio } from "../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { ContentDetails } from "../../../common/types/ContentDetails";
import { DynamicSubtitleType, mapNumberToDynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../common/types/RecordType";
import { Standard } from "../../../common/types/Standard";
import { CloudAssetStatus, mapToCloudAssetStatus } from "./CloudAssetStatus";

export enum CloudAssetDetailsTab {
    Info = 1,
    Assets = 2,
    Playback = 3
}

export type CloudAssetDetails = CloudVideoDetails | CloudAudioDetails | CloudSubtitleDetails;

interface BaseCloudAssetDetails {
    assetId: string;
    cloudFileId: string;
    deliveryId: string;
    deliveryFileName: string;
    sourceFileName?: string;
    fileRecordId?: string;
    filePath: string;
    originator?: string;
    status: CloudAssetStatus;
    content: ContentDetails;
    arrivalDate: Date;
    ingestedDate: Date;
    isTrailer: boolean;
}

export interface CloudVideoDetails extends BaseCloudAssetDetails {
    type: RecordType.Video;
    technical: CloudVideoTechnical;
    subtitle: CodedProperty;
}

export interface CloudAudioDetails extends BaseCloudAssetDetails {
    type: RecordType.Audio;
    technical: CloudAudioTechnical;
    soundtrack: CodedProperty;
}

export interface CloudSubtitleDetails extends BaseCloudAssetDetails {
    type: RecordType.Subtitle;
    subtitle: CodedProperty;
    subtitleType: DynamicSubtitleType;
    sourceSubtitleType: DynamicSubtitleType;
    inPreparation?: boolean;
}

export interface CloudVideoTechnical {
    aspectRatio: AspectRatio;
    aspectType?: AspectType;
    encoding: string;
    format: string;
    fileAspectRatio: string;
    videoBitRate?: number;
    duration: string;
    resolution?: string;
    standard?: Standard;
    framerate?: string;
}

export interface CloudAudioTechnical {
    resolution?: string;
    audioFormat?: string;
    duration?: string;
    sampleRate?: number;
    channelsCount?: number;
    audioBitRate?: number;
}

export type CloudFileRecordReference = {
    id: string;
    filePath?: string;
    fileName: string;
    fileExtension: string;
    arrivalDate: Date;
    ingestedDate: Date;
};

export const isCloudAssetDeprecated = (asset: CloudAssetDetails): boolean =>
    asset.status === CloudAssetStatus.Deprecated;

export const isInPreparation = (details: CloudAssetDetails): boolean => {
    if (!isCloudSubtitleDetails(details)) return false;

    return !!details.inPreparation;
};

export const mapToCloudAssetDetails = (res: GetCloudAssetDetailsResponse): CloudAssetDetails => {
    switch (res.type) {
        case RecordType.Video: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                subtitle: mapToCodedProperty(res.subtitle),
                technical: {
                    aspectRatio: mapToNamedProperty(res.technical.aspectRatio),
                    aspectType: res.technical.aspectType && mapToNamedProperty(res.technical.aspectType),
                    fileAspectRatio: res.technical.fileAspectRatio,
                    format: res.technical.format,
                    encoding: res.technical.encoding,
                    videoBitRate: res.technical.videoBitRate,
                    duration: res.technical.duration,
                    resolution: res.technical.resolution,
                    standard: res.technical.standard && mapToNamedProperty(res.technical.standard),
                    framerate: res.technical.framerate
                }
            };
        }

        case RecordType.Audio: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                soundtrack: mapToCodedProperty(res.soundtrack),
                technical: {
                    duration: res.technical.duration,
                    resolution: res.technical.resolution,
                    sampleRate: res.technical.sampleRate,
                    channelsCount: res.technical.channelsCount,
                    audioBitRate: res.technical.audioBitRate,
                    audioFormat: res.technical.audioFormat
                }
            };
        }

        case RecordType.Subtitle: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                subtitle: mapToCodedProperty(res.subtitle),
                subtitleType: mapNumberToDynamicSubtitleType(res.subtitleType),
                sourceSubtitleType: mapNumberToDynamicSubtitleType(res.sourceSubtitleType),
                inPreparation: res.inPreparation
            };
        }
    }
};

const mapCommonDetails = (res: GetCloudAssetDetailsResponse) => {
    return {
        assetId: res.id,
        cloudFileId: res.cloudFile.cloudFileId,
        status: mapToCloudAssetStatus(res.status),
        filePath: res.cloudFile.file.path,
        fileRecordId: res.fileRecordId,
        deliveryId: res.deliveryId,
        deliveryFileName: res.deliveryFileName,
        sourceFileName: res.sourceFileName,
        originator: res.originator,
        arrivalDate: new Date(res.arrivalDate),
        ingestedDate: new Date(res.cloudFile.ingestedDate),
        content: res.content,
        isTrailer: res.isTrailer
    };
};

export const ensureCloudVideoDetails: (details?: CloudAssetDetails) => asserts details is CloudVideoDetails = (
    details?: CloudAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Video) {
        throw new Error(`Asset record type should be video'`);
    }
};

export const ensureCloudAudioDetails: (details?: CloudAssetDetails) => asserts details is CloudAudioDetails = (
    details?: CloudAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Audio) {
        throw new Error(`Asset record type should be audio'`);
    }
};

export const ensureCloudSubtitleDetails: (details?: CloudAssetDetails) => asserts details is CloudSubtitleDetails = (
    details?: CloudAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Subtitle) {
        throw new Error(`Asset record type should be subtitle'`);
    }
};

export const isCloudSubtitleDetails = (details: CloudAssetDetails): details is CloudSubtitleDetails =>
    details.type === RecordType.Subtitle;
